import { Icon, IconID } from '@hub/common/Icon/Icon';
import { ModelViewer } from '@hub/common/ModelViewer/ModelViewer';
import { ProductMediaCarousel } from '@hub/common/ProductMediaCarousel/ProductMediaCarousel';
import type { WidenMediaElement } from '@hub/libs/widen-utils';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import { useEffect, useState } from 'react';
import { useFetchThreeDimensionalModel } from '@hub/bearing-hub/ProductDetails/ThreeDimensionalModel/useFetchThreeDimensionalModel';
import type { IbhProductionMethods, IbhShape, IbhUnitSystem } from '@igus/iglidur-bearing-hub-api';
import type { IbhDimensions } from '@hub/bearing-hub/common/types';
import type { FadeCarouselSizeDefinition } from '@hub/common/ProductMediaCarousel/ProductMediaFadeCarousel';

type ProductMediaProps = {
  produceProductMedia: WidenMediaElement[];
  isCustom: boolean;
  articleNumber: string;
  shapeKey: IbhShape;
  dimensions: IbhDimensions;
  unitSystem: IbhUnitSystem;
  productionMethod: IbhProductionMethods;
  mediaCarouselSize?: FadeCarouselSizeDefinition;
  materialColor?: string;
  onModelThumbnailGenerate?: (articleThumbnail: string) => void;
};

export const ProductMedia = ({
  produceProductMedia,
  isCustom,
  articleNumber,
  shapeKey,
  productionMethod,
  dimensions,
  unitSystem,
  mediaCarouselSize,
  materialColor,
  onModelThumbnailGenerate,
}: ProductMediaProps) => {
  const session = useSessionStore(state => state.session);
  const [showModelViewer, setShowModelViewer] = useState(isCustom);

  const {
    modelLink,
    fetchModelLink,
    error: fetchModelError,
  } = useFetchThreeDimensionalModel({
    articleNumber,
    session,
    shapeKey,
    dimensions,
    isCustom,
    productionMethod,
    unitSystem,
  });

  useEffect(() => {
    if (fetchModelError || modelLink === null) {
      setShowModelViewer(false);
    }
  }, [fetchModelError, modelLink]);
  useEffect(() => {
    if (!modelLink && showModelViewer) {
      void fetchModelLink();
    }
  }, [showModelViewer, session]);

  return showModelViewer ? (
    <ModelViewer
      configuration={{
        materialColor: materialColor || '#3d3d3d',
        modelUrl: modelLink,
        fileType: 'stl',
        width: 378,
        height: 526,
      }}
      onModelThumbnailGenerate={onModelThumbnailGenerate}
    >
      <div
        className="absolute bottom-[10px] right-[10px] cursor-pointer z-[1]"
        onClick={() => setShowModelViewer(false)}
      >
        <Icon size={10} id={IconID.PHOTO_LIBRARY} primary={false} />
      </div>
    </ModelViewer>
  ) : (
    produceProductMedia?.length && (
      <ProductMediaCarousel
        media={produceProductMedia}
        onShowModalViewer={() => setShowModelViewer(true)}
        showModelViewer={!fetchModelError}
        mediaCarouselSize={mediaCarouselSize}
      />
    )
  );
};
